import { useEffect, useRef, useState } from 'react'
import './MediaSlideshow.css'

const MediaSlideshow = ({ media }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slideDir, setSlideDir] = useState(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const timeoutRef = useRef(null)
  const dragStartX = useRef(null)
  const dragDeltaX = useRef(0)
  const isDragging = useRef(false)
  const didDrag = useRef(false)
  const suppressClick = useRef(false)
  const ANIM_MS = 420
  const SWIPE_THRESHOLD = 20

  const prevSlide = () => {
    if (isAnimating) return
    setSlideDir('prev')
    setIsAnimating(true)
  }

  const nextSlide = () => {
    if (isAnimating) return
    setSlideDir('next')
    setIsAnimating(true)
  }

  const total = media.length
  const prevIndex = (currentIndex - 1 + total) % total
  const nextIndex = (currentIndex + 1) % total
  const nextNextIndex = (currentIndex + 2) % total
  const prevPrevIndex = (currentIndex - 2 + total) % total

  useEffect(() => {
    if (!isAnimating) return
    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((prev) => {
        if (slideDir === 'prev') {
          return prev === 0 ? total - 1 : prev - 1
        }
        return prev === total - 1 ? 0 : prev + 1
      })
      setSlideDir(null)
      setIsAnimating(false)
    }, ANIM_MS)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [ANIM_MS, isAnimating, slideDir, total])

  const renderMedia = (item, index, position) =>
    item.type === 'image' ? (
      <img
        key={position}
        src={item.src}
        alt={`Slide ${index}`}
        className={`media ${position}`}
        loading={position === 'pos-current' ? 'eager' : 'lazy'}
        draggable={false}
        onDragStart={(event) => event.preventDefault()}
      />
    ) : (
      <video
        key={position}
        controls
        className={`media ${position}`}
        preload="metadata"
        playsInline
        draggable={false}
        onDragStart={(event) => event.preventDefault()}
      >
        <source src={item.src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    )

  const renderSlots = () => {
    if (!isAnimating || !slideDir) {
      return [
        renderMedia(media[prevIndex], prevIndex, 'pos-prev'),
        renderMedia(media[currentIndex], currentIndex, 'pos-current'),
        renderMedia(media[nextIndex], nextIndex, 'pos-next'),
      ]
    }

    if (slideDir === 'next') {
      return [
        renderMedia(media[prevIndex], prevIndex, 'pos-prev'),
        renderMedia(media[currentIndex], currentIndex, 'pos-current'),
        renderMedia(media[nextIndex], nextIndex, 'pos-next'),
        renderMedia(media[nextNextIndex], nextNextIndex, 'pos-incoming'),
      ]
    }

    return [
      renderMedia(media[prevPrevIndex], prevPrevIndex, 'pos-incoming'),
      renderMedia(media[prevIndex], prevIndex, 'pos-prev'),
      renderMedia(media[currentIndex], currentIndex, 'pos-current'),
      renderMedia(media[nextIndex], nextIndex, 'pos-next'),
    ]
  }

  return (
    <div className="mediaContainer">
      <div className="mediaWrapper">
        <div
          className={`mediaTrack ${
            slideDir ? `anim-${slideDir}` : 'anim-idle'
          }`}
          onClickCapture={(event) => {
            if (!suppressClick.current) return
            event.preventDefault()
            event.stopPropagation()
          }}
          onPointerDown={(event) => {
            if (isAnimating) return
            dragStartX.current = event.clientX
            dragDeltaX.current = 0
            isDragging.current = true
            didDrag.current = false
          }}
          onPointerMove={(event) => {
            if (!isDragging.current || dragStartX.current == null) return
            dragDeltaX.current = event.clientX - dragStartX.current
            if (Math.abs(dragDeltaX.current) > 4) {
              didDrag.current = true
            }
          }}
          onPointerUp={() => {
            if (!isDragging.current) return
            const delta = dragDeltaX.current
            isDragging.current = false
            dragStartX.current = null
            dragDeltaX.current = 0
            if (didDrag.current) {
              suppressClick.current = true
              requestAnimationFrame(() => {
                suppressClick.current = false
              })
            }
            if (Math.abs(delta) < SWIPE_THRESHOLD) return
            if (delta > 0) {
              prevSlide()
            } else {
              nextSlide()
            }
          }}
          onPointerLeave={() => {
            isDragging.current = false
            dragStartX.current = null
            dragDeltaX.current = 0
          }}
        >
          {renderSlots()}
        </div>
      </div>
      <div className="navButtons">
        <button onClick={prevSlide} className="navButton">
          ❮
        </button>
        <button onClick={nextSlide} className="navButton">
          ❯
        </button>
      </div>
    </div>
  )
}

export default MediaSlideshow
